import React, { lazy } from 'react'
import { CardStyleInterpolators, StackNavigationOptions } from '@react-navigation/stack'
import {
  ActivityDetailModal,
  ActivityModal,
  AddLabResultsScreen,
  ChangePasswordScreen,
  ClaimFreeVideoCallModal,
  DexcomConnectionSuccessfulScreen,
  DexcomSensorSettingsScreen,
  EarlierSlotAvailableModal,
  EditAccountDetailsScreen,
  EditEmailScreen,
  EducationScreen,
  EventsEditScreen,
  FastingWindowModal,
  FreePassLateRescheduleOrCancelVideoCallModal,
  GlucoseThresholdsModal,
  IntegrationsScreen,
  LateRescheduleOrCancelVideoCallModal,
  LearnScreen,
  LessonExperimentModal,
  LessonScreen,
  MacroGoalsModal,
  ManualCalibrationModal,
  MeasurementModal,
  ModuleCompletionScreen,
  ModuleScreen,
  OnboardingChecklistScreen,
  ProgramScreen,
  ReferralScreen,
  RescheduleOrCancelVideoCallModal,
  ScansScreen,
  ScheduleCallOrPurchasePlanModal,
  SensorSettingsScreen,
  SkipTutorialScreen,
  SourcePriorityListScreen,
  SteloCheckoutScreen,
  SteloSetupScreen,
  SubscriptionScreen,
  TerraFitbitGoogleHealthAnnouncementScreen,
  TerraProviderScreen,
  ThresholdsModal,
  UploadLabResultsScreen,
  UploadLabResultsSuccessScreen,
  UserSettingsScreen,
  VideoCallCheckoutScreen,
  VideoCallModal,
  VideoCallsGettingStartedScreen,
  VideoCallsStartModal,
  VideoCallsUpsellDetailsModal,
  VideoCallsUpsellModal,
} from '@screens'
import { CalendarModal } from '@src/navigation/containers/CalendarModal'
import { TutorialModal, WebViewModal } from '@components'
import { Debug } from '@config'
import { DrawerNavigator } from '@src/navigation'
import { ActionsModal } from '@src/navigation/containers/ActionsModal'
import { IntercomMenuModal } from '@src/navigation/containers/IntercomMenuModal'
import {
  fadeFromBottomModalOptions,
  fadeInModalOptions,
  gestureDismissibleModalOptions,
  LAZY_SCREEN_PATHS,
  tutorialModalOptions,
} from '@src/navigation/utils'
import { ShareViewModal } from '@src/containers/ShareViewModal'
import { CancelModal } from '@src/components/CancelModal'
import { CustomIngredient, IngredientPortion, IngredientSearch } from '@src/screens/Ingredients'
import {
  CompareMeals,
  CompareMealsDetails,
  CreateMealModal,
  EditMealModal,
  MealDetailModal,
  ShareMealComparison,
  UnlockNutritionistSupport,
} from '@src/screens/Meals'
import {
  AddonCheckoutScreen,
  CheckoutWithInsuranceOptionScreen,
  DietitianMenuCheckoutScreen,
  DietitianMenuFeedbackModal,
  InsuranceCallsUpsellScreen,
  OrderConfirmedScreen,
  PauseSubscriptionReviewScreen,
  PauseSubscriptionScreen,
  PauseSubscriptionSuccessScreen,
  ProductsSelectionModal,
  ProgramDetailsScreen,
  SteloAddonOfferScreen,
  SubscriptionCheckoutScreen,
} from '@src/screens/Marketplace'
import {
  HealthKitIntegrationScreen,
  KetoMojoIntegrationScreen,
  SettingsScreen,
} from '@src/screens/Settings'
import { TutorialsGroup } from '@src/screens/Tutorials/containers/TutorialsGroup'
import { CMSTutorial } from '@src/screens/Tutorials/containers/CMSTutorial'
import { VideoTutorial } from '@src/screens/Tutorials/containers/VideoTutorial'
import { JournalEntryModal } from '@src/screens/JournalEntries'
import { EditDailyMeasurementModal } from '@src/screens/DailyMeasurements/components/EditDailyMeasurementModal'
import { ChatWelcomeModal } from '@src/screens/Chat/components/ChatWelcomeModal'
import { ChatDelimiterConfirmationModal } from '@src/screens/Chat/components/ChatDelimiterConfirmationModal'
import { JournalEngagementPromptModal } from '@src/screens/JournalEntries/containers/JournalEngagementPromptModal'
import {
  AddReminderModal,
  EditReminderModal,
  NotificationsSettingsScreen,
} from '@src/screens/Settings/components/Notifications'
import { NotificationInboxScreen } from '@src/screens/NotificationInbox'
import { SubscriptionsNavigator } from '@src/navigation/navigators/subscriptions.navigator'
import { ChooseGoalModal } from '@src/screens/Insights/components/ChooseGoalModal'
import { ChoosePrimaryGoalMetricsModal } from '@src/screens/Insights/components/ChoosePrimaryGoalMetricsModal'
import { InsightsDetailsScreen, InsightsSettings } from '@src/screens/Insights'
import { RateOurAppModal } from '@src/navigation/containers/RateOurAppModal'
import { RateAppModal } from '@src/navigation/containers/RateAppModal'
import { PositiveCoachRatingRateAppModal } from '@src/navigation/containers/PositiveCoachRatingRateAppModal'
import { PositiveRatingModal } from '@src/navigation/containers/PositiveRatingModal'
import {
  CallSchedulingConfirmationScreen,
  CallSchedulingContainer,
  CheckInCompletedModal,
  FeePaymentIncompleteModal,
  FeePaymentScreen,
  FeePaymentSuccessfulModal,
  FreePassLateRescheduleVideoCallModal,
  LateRescheduleVideoCallModal,
  MonthlyNutritionCoachingCheckoutScreen,
  CoachSwitchScreen,
  PaidCallsIntroScreen,
  PaymentFailed,
  ReferralVideoCallNotLicensedScreen,
  ReferralVideoCallScreen,
  RescheduleVideoCallToEarlierSlotScreen,
  SetWeightLossGoalScreen,
  SubmitInsuranceCardScreen,
  VideoCallCheckIn,
  VideoCallCheckInAddendum,
} from '@src/screens/NutritionistHub'
import { BiomarkerForm, BiomarkersScreen, LabResultsScreen } from '@src/screens/LabResults'
import { MemberNoteDetail } from '@src/screens/NutritionistHub/components/MemberNotes/MemberNoteDetail'
import { MemberNotes } from '@src/screens/NutritionistHub/components/MemberNotes/MemberNotes'
import {
  AllVideoCalls,
  UpsertRecurrence,
  UpsertRecurrenceSuccessfulModal,
} from '@screens/NutritionistHub/components/UpcomingCalls'
import { SetReminderModal } from '@src/screens/LabResults/components/SetReminderModal'
import {
  EligibilityCheckScreen,
  FreeTrialSubscriptionOfferScreen,
  ProgramErrorScreen,
  SteloSelectSubscriptionScreen,
} from '@src/screens/SignUp'
import {
  CheckConnectionScreen,
  ConfirmSensorSelectionScreen,
  HealthPermissionScreen,
} from '@src/screens/OwnSensorOnboarding'
import { ConnectionSuccessfulScreen } from '@src/screens/OwnSensorOnboarding/ConnectionSuccessfulScreen'
import { InsuranceStatusScreen } from '@src/screens/InsuranceStatus'
import { PolicyConsentScreen, RequiredPolicyConsentScreen } from '@src/screens/PolicyConsents'
import { InsurancePolicyForm } from '@src/screens/InsuranceStatus/components/InsurancePolicyForm'
import { InsuranceNotEligibleModal } from '@src/screens/NutritionistHub/components/InsuranceNotEligibleModal'
import { NutritionSummaryModal } from '@src/components/NutritionSummaryCard/NutritionSummaryModal'
import { ChartModalContainer as ChartModal } from '@src/screens/Events/containers/ChartModal'
import { GlucoseScoreModal } from '@src/screens/Events/components/GlucoseScore/GlucoseScoreModal'
import { TimelineTutorialModal } from '@src/screens/Events/components/TimelineTutorialModal'
import { NutritionGuidanceUpsellModal } from '@src/screens/Home/components/NutritionGuidanceUpsellModal'
// eslint-disable-next-line max-len
import { FreeNutritionistConsultationModal } from '@src/screens/Home/components/FreeNutritionistConsultationModal'
import { RateCoachModal } from '@src/screens/NutritionistHub/components/RateCoachModal'
import { ProductExplanation } from '@screens/Marketplace/components/NewMarketplace/ProductExplanation.tsx'
// eslint-disable-next-line max-len
import { OwnSensorActivatedNutritionistUpsellModal } from '@src/screens/Home/components/OwnSensorActivatedNutritionistUpsellModal'
// eslint-disable-next-line max-len
import { WeightLossExperienceDesiredWeightCompletedScreen } from '@src/screens/WeightLossExperience/DesiredWeightCompletedScreen'
import { WeightLossExperiencePersonalPlanScreen } from '@src/screens/WeightLossExperience/PersonalPlan'
import { SteloUpsellScreen } from '@src/screens/SteloUpsell'
import { PurchaseFreeByosProduct } from '@screens/Marketplace/components/NewMarketplace/PurchaseFreeByosProduct.tsx'
import { MfpInstructionsScreen } from '@src/screens/Settings/components/Integrations/MyFitnessPal/MfpInstructionsScreen'
// eslint-disable-next-line max-len
import { ReferredToPurchasedByNutritionistModal } from '@src/screens/Home/components/ReferredToPurchasedByNutritionistModal'
import { ChurnFeedbackModal } from '@src/screens/NutritionistHub/components/ChurnFeedbackModal'
import { NoMoreCallsLeftModal } from '@screens/NutritionistHub/components/NoMoreCallsLeftModal.tsx'
// eslint-disable-next-line max-len
import { LateCancelOrReschedulePaidVideoCallModal } from '@screens/NutritionistHub/components/LateCancelOrReschedulePaidVideoCallModal.tsx'
import { SendMessageToCoach } from '@src/screens/SendMessageToCoach'
import { GiveFeedbackModal } from '@src/screens/Questionnaire'
import { UhcSubscriptionExpiredModal } from '@screens/Uhc'
import { EventsListScreen } from '@src/screens/Events/screens/EventsListScreen'
import { CustomizeHomeModal } from '@src/screens/Home/components/CustomizeHomeModal'
import { TrustpilotReview } from '@screens/TrustpilotReview'
import { CompletedScreen } from '@screens/CompletedScreen'
import { ReferralPopup } from '@screens/Referral'
// eslint-disable-next-line max-len
import { UnifiedOnboardingSelectNutritionist } from '@src/screens/Onboarding/screens/UnifiedOnboardingSelectNutritionist'
import { DrawerRoute } from '../components/drawer/hooks'

const AdminSettingsScreen = lazy(LAZY_SCREEN_PATHS.AdminSettings)
const BarcodeIngredientSearchModal = lazy(LAZY_SCREEN_PATHS.BarcodeIngredientSearch)
const ChatScreen = lazy(LAZY_SCREEN_PATHS.Chat)
const CancelSubscriptionConfirmationScreen = lazy(LAZY_SCREEN_PATHS.CancelSubscriptionConfirmation)
const CancelSubscriptionOnCommitmentScreen = lazy(LAZY_SCREEN_PATHS.CancelSubscriptionOnCommitment)
const CancelSubscriptionOtherOptionsScreen = lazy(LAZY_SCREEN_PATHS.CancelSubscriptionOtherOptions)
const ChangeBillingCycleModal = lazy(LAZY_SCREEN_PATHS.ChangeBillingCycleModal)
const ChangeBillingCycleSuccessModal = lazy(LAZY_SCREEN_PATHS.ChangeBillingCycleSuccessModal)
const ChurnRecoveryMembershipOfferScreen = lazy(LAZY_SCREEN_PATHS.ChurnRecoveryMembershipOffer)
const ChurnMonthlyCGMOfferScreen = lazy(LAZY_SCREEN_PATHS.ChurnMonthlyCGMOffer)
const ChurnRecoveryOptionsScreen = lazy(LAZY_SCREEN_PATHS.ChurnRecoveryOptions)
const ChurnRecoveryProactiveUpsellOfferModal = lazy(
  LAZY_SCREEN_PATHS.ChurnRecoveryProactiveUpsellOffer,
)
const ChurnRecoveryUpsellOfferScreen = lazy(LAZY_SCREEN_PATHS.ChurnRecoveryUpsellOffer)
const ChurnRecoveryUpsellOfferConfirmationScreen = lazy(
  LAZY_SCREEN_PATHS.ChurnRecoveryUpsellOfferConfirmation,
)
const ConfirmWeightLossRecurrenceScreen = lazy(LAZY_SCREEN_PATHS.ConfirmWeightLossRecurrence)
const LabResultDetailScreen = lazy(LAZY_SCREEN_PATHS.LabResultDetail)
const ManageSubscriptionsScreen = lazy(LAZY_SCREEN_PATHS.ManageSubscriptions)
const OpenSourceLicensesScreen = lazy(LAZY_SCREEN_PATHS.OpenSourceLicenses)
const OpenSourceLicenseDetailScreen = lazy(LAZY_SCREEN_PATHS.OpenSourceLicenseDetail)
const CoachingCallCreditBalanceModal = lazy(LAZY_SCREEN_PATHS.CoachingCallCreditBalanceModal)
const ManageUpcomingSubscriptionModal = lazy(LAZY_SCREEN_PATHS.ManageUpcomingSubscriptionModal)
const ReactivateSubscriptionModal = lazy(LAZY_SCREEN_PATHS.ReactivateSubscriptionModal)
const ResumeSubscriptionModal = lazy(LAZY_SCREEN_PATHS.ResumeSubscriptionModal)
const StressManagementIntroScreen = lazy(LAZY_SCREEN_PATHS.StressManagementIntroScreen)
const ScheduleNextCallModal = lazy(LAZY_SCREEN_PATHS.ScheduleNextCallModal)
const LabPanelsScreen = lazy(LAZY_SCREEN_PATHS.LabPanelsScreen)
const MicrobiomeTest = lazy(LAZY_SCREEN_PATHS.MicrobiomeTest)
const LabPanelRecurrence = lazy(LAZY_SCREEN_PATHS.LabPanelRecurrence)
const LabPanelsOrderConfirmed = lazy(LAZY_SCREEN_PATHS.LabPanelsOrderConfirmed)
const CoachSwitchSpecialtiesFilterModal = lazy(LAZY_SCREEN_PATHS.CoachSwitchSpecialtiesFilterModal)
const LabPanelsUpsellModal = lazy(LAZY_SCREEN_PATHS.LabPanelsUpsellModal)
const FreeCallTrialUpsellPopup = lazy(LAZY_SCREEN_PATHS.FreeCallTrialUpsellPopup)
const CoachSwitchModal = lazy(LAZY_SCREEN_PATHS.NutritionistSwitchModal)
const CoachSwitchSeeAllCoachesScreen = lazy(LAZY_SCREEN_PATHS.CoachSwitchSeeAllCoachesScreen)
const CallSchedulingSeeAllCoachesScreen = lazy(LAZY_SCREEN_PATHS.CallSchedulingSeeAllCoachesScreen)
const CoachSwitchSummaryScreen = lazy(LAZY_SCREEN_PATHS.CoachSwitchSummaryScreen)
const CallSchedulingReviewScreen = lazy(LAZY_SCREEN_PATHS.CallSchedulingReviewScreen)
const CancelRecurringAppointmentModal = lazy(LAZY_SCREEN_PATHS.CancelRecurringAppointmentModal)
const ActionGoalFormModal = lazy(LAZY_SCREEN_PATHS.ActionGoalFormModal)
const ActionGoalListScreen = lazy(LAZY_SCREEN_PATHS.ActionGoalListScreen)
const UpsertQuickPromptModal = lazy(LAZY_SCREEN_PATHS.UpsertQuickPromptModal)
const ReportNoteModal = lazy(LAZY_SCREEN_PATHS.ReportNoteModal)
const CustomReportModal = lazy(LAZY_SCREEN_PATHS.CustomReportModal)
const NoraConversationScreen = lazy(LAZY_SCREEN_PATHS.NoraConversationScreen)
const NoraConversationsScreen = lazy(LAZY_SCREEN_PATHS.NoraConversationsScreen)
const NoraQuickPromptsScreen = lazy(LAZY_SCREEN_PATHS.NoraQuickPromptsScreen)
const NoraReportScreen = lazy(LAZY_SCREEN_PATHS.NoraReportScreen)
const NoraReportsScreen = lazy(LAZY_SCREEN_PATHS.NoraReportsScreen)
const NoraChatPreferencesSettingsScreen = lazy(LAZY_SCREEN_PATHS.NoraChatPreferencesSettingsScreen)
const NoraNotificationSettingsScreen = lazy(LAZY_SCREEN_PATHS.NoraNotificationSettingsScreen)
const NoraSettingsScreen = lazy(LAZY_SCREEN_PATHS.NoraSettingsScreen)

// TODO: Use corresponding type for AppStack
export const getAppNavigator = (
  AppStack: any,
  options: StackNavigationOptions,
  hasLargeScreen: boolean,
  drawerRoutes: DrawerRoute[],
) => {
  return (
    <>
      {/* Drawer screen should be the first one */}
      <AppStack.Screen options={options} name="Drawer" component={DrawerNavigator} />

      <AppStack.Group
        screenOptions={{
          cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS,
          presentation: 'transparentModal',
        }}
      >
        <AppStack.Screen name="AddJournalEntry" component={JournalEntryModal} />
        <AppStack.Screen name="OrderConfirmed" component={OrderConfirmedScreen} />
        <AppStack.Screen name="AddActivity" component={ActivityModal} />
        <AppStack.Screen
          name="AddMeal"
          component={CreateMealModal}
          options={{ gestureEnabled: false }}
        />
        <AppStack.Screen name="EventsListScreen" component={EventsListScreen} />
        <AppStack.Screen name="AddMeasurement" component={MeasurementModal} />
        <AppStack.Screen name="BarcodeIngredientSearch" component={BarcodeIngredientSearchModal} />
        <AppStack.Screen name="ShareViewModal" component={ShareViewModal} />
        <AppStack.Screen name="EducationModal" component={EducationScreen} />
        <AppStack.Screen name="OnboardingChecklist" component={OnboardingChecklistScreen} />
        <AppStack.Screen name="ActionGoalForm" component={ActionGoalFormModal} />
        <AppStack.Screen name="ActionGoalListScreen" component={ActionGoalListScreen} />
        <AppStack.Screen
          name="StressManagementIntroScreen"
          component={StressManagementIntroScreen}
        />
      </AppStack.Group>
      <AppStack.Group screenOptions={gestureDismissibleModalOptions}>
        <AppStack.Screen name="ChooseGoalModal" component={ChooseGoalModal} />
        <AppStack.Screen name="IngredientPortion" component={IngredientPortion} />
        <AppStack.Screen name="ResumeSubscriptionModal" component={ResumeSubscriptionModal} />
        <AppStack.Screen
          name="ReactivateSubscriptionModal"
          component={ReactivateSubscriptionModal}
        />
        <AppStack.Screen name="ChangeBillingCycleModal" component={ChangeBillingCycleModal} />
        <AppStack.Screen name="AddReminderModal" component={AddReminderModal} />
        <AppStack.Screen name="EditReminderModal" component={EditReminderModal} />
        <AppStack.Screen name="PaymentFailed" component={PaymentFailed} />
        <AppStack.Screen
          name="FeePaymentScreen"
          component={FeePaymentScreen}
          options={{ gestureEnabled: false }}
        />
        <AppStack.Screen
          name="ScheduleCallOrPurchasePlanModal"
          component={ScheduleCallOrPurchasePlanModal}
        />
      </AppStack.Group>
      <AppStack.Group screenOptions={fadeInModalOptions}>
        <AppStack.Screen name="ShowActions" component={ActionsModal} />
        <AppStack.Screen name="GlucoseScoreModal" component={GlucoseScoreModal} />
        <AppStack.Screen name="NutritionSummaryModal" component={NutritionSummaryModal} />
        <AppStack.Screen name="JournalEngagementPrompt" component={JournalEngagementPromptModal} />
      </AppStack.Group>
      <AppStack.Group screenOptions={fadeFromBottomModalOptions}>
        <AppStack.Screen
          name="ChurnRecoveryProactiveUpsellOffer"
          component={ChurnRecoveryProactiveUpsellOfferModal}
        />
        <AppStack.Screen name="TimelineTutorialModal" component={TimelineTutorialModal} />
        <AppStack.Screen name="GiveFeedbackModal" component={GiveFeedbackModal} />
        <AppStack.Screen
          name="ChoosePrimaryGoalMetricsModal"
          component={ChoosePrimaryGoalMetricsModal}
        />
        <AppStack.Screen
          name="LateCancelOrReschedulePaidVideoCallModal"
          component={LateCancelOrReschedulePaidVideoCallModal}
        />
        <AppStack.Screen
          name="RescheduleOrCancelVideoCallModal"
          component={RescheduleOrCancelVideoCallModal}
        />
        <AppStack.Screen
          name="CancelRecurringAppointmentModal"
          component={CancelRecurringAppointmentModal}
        />
        <AppStack.Screen
          name="LateRescheduleOrCancelVideoCallModal"
          component={LateRescheduleOrCancelVideoCallModal}
        />
        <AppStack.Screen
          name="FreePassLateRescheduleOrCancelVideoCallModal"
          component={FreePassLateRescheduleOrCancelVideoCallModal}
        />
        <AppStack.Screen
          name="LateRescheduleVideoCallModal"
          component={LateRescheduleVideoCallModal}
        />
        <AppStack.Screen
          name="FreePassLateRescheduleVideoCallModal"
          component={FreePassLateRescheduleVideoCallModal}
        />
        <AppStack.Screen name="FeePaymentSuccessfulModal" component={FeePaymentSuccessfulModal} />
        <AppStack.Screen name="FeePaymentIncompleteModal" component={FeePaymentIncompleteModal} />
        <AppStack.Screen name="ClaimFreeVideoCallModal" component={ClaimFreeVideoCallModal} />
        <AppStack.Screen name="EarlierSlotAvailableModal" component={EarlierSlotAvailableModal} />
        <AppStack.Screen name="InsuranceNotEligibleModal" component={InsuranceNotEligibleModal} />
        <AppStack.Screen name="VideoCallModal" component={VideoCallModal} />
        <AppStack.Screen name="VideoCallsStartModal" component={VideoCallsStartModal} />
        <AppStack.Screen name="VideoCallCheckIn" component={VideoCallCheckIn} />
        <AppStack.Screen name="NoMoreCallsLeftModal" component={NoMoreCallsLeftModal} />
        <AppStack.Screen
          name="CoachingCallCreditBalanceModal"
          component={CoachingCallCreditBalanceModal}
        />
        <AppStack.Screen
          name="UhcSubscriptionExpiredModal"
          component={UhcSubscriptionExpiredModal}
        />
        <AppStack.Screen name="VideoCallCheckInAddendum" component={VideoCallCheckInAddendum} />
        <AppStack.Screen name="CheckInCompletedModal" component={CheckInCompletedModal} />
        <AppStack.Screen name="RateCoachModal" component={RateCoachModal} />
        <AppStack.Screen name="ChurnFeedbackModal" component={ChurnFeedbackModal} />
        <AppStack.Screen name="ChangeMacroGoals" component={MacroGoalsModal} />
        <AppStack.Screen name="GlucoseThresholdSettings" component={GlucoseThresholdsModal} />
        <AppStack.Screen name="CustomizeHomeModal" component={CustomizeHomeModal} />
        <AppStack.Screen name="IntercomMenu" component={IntercomMenuModal} />
        <AppStack.Screen name="ChangeDateRange" component={CalendarModal} />
        <AppStack.Screen name="CancelModal" component={CancelModal} />
        <AppStack.Screen name="RateOurAppModal" component={RateOurAppModal} />
        <AppStack.Screen name="RateAppModal" component={RateAppModal} />
        <AppStack.Screen
          name="PositiveCoachRatingRateAppModal"
          component={PositiveCoachRatingRateAppModal}
        />
        <AppStack.Screen name="PositiveRatingModal" component={PositiveRatingModal} />
        <AppStack.Screen name="ThresholdSettings" component={ThresholdsModal} />
        <AppStack.Screen name="CalibrateSensor" component={ManualCalibrationModal} />
        <AppStack.Screen name="ChangeFastingWindow" component={FastingWindowModal} />
        <AppStack.Screen name="ProductsSelection" component={ProductsSelectionModal} />
        <AppStack.Screen name="UnlockNutritionistSupport" component={UnlockNutritionistSupport} />
        <AppStack.Screen name="NutritionistSwitchModal" component={CoachSwitchModal} />
        <AppStack.Screen name="ShareMealComparison" component={ShareMealComparison} />
        <AppStack.Screen name="WebViewModal" component={WebViewModal} />
        <AppStack.Screen name="TutorialsGroup" component={TutorialsGroup} />
        <AppStack.Screen name="ZoomChart" component={ChartModal} />
        <AppStack.Screen name="ChatWelcomeModal" component={ChatWelcomeModal} />
        <AppStack.Screen
          name="ChatDelimiterConfirmationModal"
          component={ChatDelimiterConfirmationModal}
        />
        <AppStack.Screen
          name="ChangeBillingCycleSuccessModal"
          component={ChangeBillingCycleSuccessModal}
        />
        <AppStack.Screen
          name="ManageUpcomingSubscriptionModal"
          component={ManageUpcomingSubscriptionModal}
        />
        <AppStack.Screen name="SetReminderModal" component={SetReminderModal} />
        <AppStack.Screen name="VideoCallsUpsellModal" component={VideoCallsUpsellModal} />
        <AppStack.Screen
          name="VideoCallsUpsellDetailsModal"
          component={VideoCallsUpsellDetailsModal}
        />
        <AppStack.Screen
          name="NutritionGuidanceUpsellModal"
          component={NutritionGuidanceUpsellModal}
        />
        <AppStack.Screen
          name="FreeNutritionistConsultationModal"
          component={FreeNutritionistConsultationModal}
        />
        <AppStack.Screen
          name="OwnSensorActivatedNutritionistUpsellModal"
          component={OwnSensorActivatedNutritionistUpsellModal}
        />
        <AppStack.Screen name="RequiredPolicyConsent" component={RequiredPolicyConsentScreen} />
        <AppStack.Screen name="PolicyConsent" component={PolicyConsentScreen} />
        <AppStack.Screen
          name="UpsertRecurrenceSuccessfulModal"
          component={UpsertRecurrenceSuccessfulModal}
        />
        <AppStack.Screen
          name="ReferredToPurchasedByNutritionistModal"
          component={ReferredToPurchasedByNutritionistModal}
        />
        <AppStack.Screen name="ScheduleNextCallModal" component={ScheduleNextCallModal} />
        <AppStack.Screen name="UpsertQuickPromptModal" component={UpsertQuickPromptModal} />
        <AppStack.Screen name="ReportNoteModal" component={ReportNoteModal} />
        <AppStack.Screen name="CustomReportModal" component={CustomReportModal} />
        <AppStack.Screen
          name="CoachSwitchSpecialtiesFilterModal"
          component={CoachSwitchSpecialtiesFilterModal}
        />
        <AppStack.Screen name="LabPanelsUpsellModal" component={LabPanelsUpsellModal} />
        <AppStack.Screen name="FreeCallTrialUpsellPopup" component={FreeCallTrialUpsellPopup} />
      </AppStack.Group>
      {!hasLargeScreen &&
        drawerRoutes.map(({ routeName, component }) => (
          <AppStack.Screen key={routeName} name={routeName} component={component} />
        ))}

      <AppStack.Group
        screenOptions={{
          presentation: 'card',
        }}
      >
        <AppStack.Screen name="ActivityDetail" component={ActivityDetailModal} />
        <AppStack.Screen name="EditJournalEntry" component={JournalEntryModal} />
        <AppStack.Screen name="EditMeasurement" component={MeasurementModal} />
        <AppStack.Screen name="MealDetail" component={MealDetailModal} />
        <AppStack.Screen name="EditMeal" component={EditMealModal} />
        <AppStack.Screen name="EditDailyMeasurementModal" component={EditDailyMeasurementModal} />
        <AppStack.Screen name="CompareMeals" component={CompareMeals} />
        <AppStack.Screen name="CompareMealsDetails" component={CompareMealsDetails} />
        <AppStack.Screen name="SteloSetup" component={SteloSetupScreen} />
        <AppStack.Screen
          name="ShowTutorial"
          component={TutorialModal}
          options={tutorialModalOptions}
        />

        <AppStack.Screen name="AppSettings" component={SettingsScreen} />
        <AppStack.Screen name="OpenSourceLicenses" component={OpenSourceLicensesScreen} />
        <AppStack.Screen name="OpenSourceLicenseDetail" component={OpenSourceLicenseDetailScreen} />
        <AppStack.Screen name="FriendReferral" component={ReferralScreen} />
        <AppStack.Screen name="ReferralPopup" component={ReferralPopup} />
        <AppStack.Screen name="Subscriptions" component={SubscriptionsNavigator} />
        <AppStack.Screen name="ProductExplanation" component={ProductExplanation} />
        <AppStack.Screen
          name="FreeTrialSubscriptionOffer"
          component={FreeTrialSubscriptionOfferScreen}
        />
        <AppStack.Screen name="EligibilityCheck" component={EligibilityCheckScreen} />
        <AppStack.Screen name="IngredientSearch" component={IngredientSearch} />
        <AppStack.Screen name="CustomIngredient" component={CustomIngredient} />
        <AppStack.Screen name="Scans" component={ScansScreen} />
        <AppStack.Screen name="EventsEdit" component={EventsEditScreen} />
        <AppStack.Screen name="AccountSubscriptions" component={SubscriptionScreen} />
        <AppStack.Screen name="ChangePassword" component={ChangePasswordScreen} />
        <AppStack.Screen name="AddLabResults" component={AddLabResultsScreen} />
        <AppStack.Screen name="LabResultDetail" component={LabResultDetailScreen} />
        <AppStack.Screen name="UploadLabResults" component={UploadLabResultsScreen} />
        <AppStack.Screen name="UploadLabResultsSuccess" component={UploadLabResultsSuccessScreen} />
        <AppStack.Screen name="EditAccountDetails" component={EditAccountDetailsScreen} />
        <AppStack.Screen name="EditEmail" component={EditEmailScreen} />
        <AppStack.Screen name="ProgramDetails" component={ProgramDetailsScreen} />
        <AppStack.Screen name="ManageSubscriptions" component={ManageSubscriptionsScreen} />
        <AppStack.Screen name="PurchaseFreeByosProduct" component={PurchaseFreeByosProduct} />
        <AppStack.Screen name="SubscriptionCheckout" component={SubscriptionCheckoutScreen} />
        <AppStack.Screen
          name="CancelSubscriptionOtherOptions"
          component={CancelSubscriptionOtherOptionsScreen}
        />
        <AppStack.Screen
          name="CancelSubscriptionOnCommitment"
          component={CancelSubscriptionOnCommitmentScreen}
        />
        <AppStack.Screen
          name="CancelSubscriptionConfirmation"
          component={CancelSubscriptionConfirmationScreen}
        />
        <AppStack.Screen name="PauseSubscription" component={PauseSubscriptionScreen} />
        <AppStack.Screen name="PauseSubscriptionReview" component={PauseSubscriptionReviewScreen} />
        <AppStack.Screen
          name="PauseSubscriptionSuccess"
          component={PauseSubscriptionSuccessScreen}
        />
        <AppStack.Screen name="ChurnRecoveryOptions" component={ChurnRecoveryOptionsScreen} />
        <AppStack.Screen
          name="ChurnRecoveryUpsellOffer"
          component={ChurnRecoveryUpsellOfferScreen}
        />
        <AppStack.Screen
          name="ChurnRecoveryUpsellOfferConfirmation"
          component={ChurnRecoveryUpsellOfferConfirmationScreen}
        />
        <AppStack.Screen
          name="ChurnRecoveryMembershipOffer"
          component={ChurnRecoveryMembershipOfferScreen}
        />
        <AppStack.Screen name="ChurnMonthlyCGMOffer" component={ChurnMonthlyCGMOfferScreen} />
        <AppStack.Screen name="NutritionistSwitchScreen" component={CoachSwitchScreen} />
        <AppStack.Screen
          name="CoachSwitchSeeAllCoachesScreen"
          component={CoachSwitchSeeAllCoachesScreen}
        />
        <AppStack.Screen
          name="CallSchedulingSeeAllCoachesScreen"
          component={CallSchedulingSeeAllCoachesScreen}
        />
        <AppStack.Screen name="CoachSwitchSummaryScreen" component={CoachSwitchSummaryScreen} />
        <AppStack.Screen name="InsuranceCallsUpsellScreen" component={InsuranceCallsUpsellScreen} />
        <AppStack.Screen name="UserSettings" component={UserSettingsScreen} />
        <AppStack.Screen name="NotificationInbox" component={NotificationInboxScreen} />
        <AppStack.Screen name="NotificationsSettings" component={NotificationsSettingsScreen} />
        <AppStack.Screen name="SensorSettings" component={SensorSettingsScreen} />
        <AppStack.Screen name="DexcomSensorSettings" component={DexcomSensorSettingsScreen} />
        <AppStack.Screen
          name="DexcomConnectionSuccessful"
          component={DexcomConnectionSuccessfulScreen}
        />
        <AppStack.Screen name="SourcePriorityList" component={SourcePriorityListScreen} />
        <AppStack.Screen name="Integrations" component={IntegrationsScreen} />
        <AppStack.Screen name="MfpInstructions" component={MfpInstructionsScreen} />
        <AppStack.Screen name="SkipTutorial" component={SkipTutorialScreen} />
        <AppStack.Screen
          name="TerraFitbitGoogleHealthAnnouncement"
          component={TerraFitbitGoogleHealthAnnouncementScreen}
        />
        <AppStack.Screen name="TerraProvider" component={TerraProviderScreen} />
        <AppStack.Screen name="HealthKitIntegration" component={HealthKitIntegrationScreen} />
        <AppStack.Screen name="KetoMojoIntegration" component={KetoMojoIntegrationScreen} />
        {Debug.useAdminMenu && (
          <AppStack.Screen name="AdminSettings" component={AdminSettingsScreen} />
        )}

        <AppStack.Screen name="CoursesLearn" component={LearnScreen} />
        <AppStack.Screen name="CoursesProgram" component={ProgramScreen} />
        <AppStack.Screen name="CoursesModule" component={ModuleScreen} />
        <AppStack.Screen name="CoursesLesson" component={LessonScreen} />
        <AppStack.Screen
          name="CoursesLessonItem"
          component={LessonScreen}
          options={{ animationEnabled: false }}
        />
        <AppStack.Screen
          name="CoursesModuleCompletion"
          component={ModuleCompletionScreen}
          options={{ animationEnabled: false }}
        />
        <AppStack.Screen name="CoursesExperiment" component={LessonExperimentModal} />
        <AppStack.Screen name="CMSTutorial" component={CMSTutorial} />
        <AppStack.Screen name="CheckConnection" component={CheckConnectionScreen} />
        <AppStack.Screen name="ConnectionSuccessful" component={ConnectionSuccessfulScreen} />
        <AppStack.Screen name="VideoTutorial" component={VideoTutorial} />
        <AppStack.Screen name="Chat" component={ChatScreen} />
        <AppStack.Screen name="NoraConversationScreen" component={NoraConversationScreen} />
        <AppStack.Screen name="NoraConversationsScreen" component={NoraConversationsScreen} />
        <AppStack.Screen name="NoraQuickPromptsScreen" component={NoraQuickPromptsScreen} />
        <AppStack.Screen name="NoraReportScreen" component={NoraReportScreen} />
        <AppStack.Screen name="NoraReportsScreen" component={NoraReportsScreen} />
        <AppStack.Screen
          name="NoraChatPreferencesSettingsScreen"
          component={NoraChatPreferencesSettingsScreen}
        />
        <AppStack.Screen
          name="NoraNotificationSettingsScreen"
          component={NoraNotificationSettingsScreen}
        />
        <AppStack.Screen name="NoraSettingsScreen" component={NoraSettingsScreen} />
        <AppStack.Screen name="CallScheduling" component={CallSchedulingContainer} />
        <AppStack.Screen
          name="RescheduleVideoCallToEarlierSlot"
          component={RescheduleVideoCallToEarlierSlotScreen}
        />
        <AppStack.Screen name="ActivateSensorTutorialsGroup" component={TutorialsGroup} />
        <AppStack.Screen
          name="VideoCallsGettingStartedScreen"
          component={VideoCallsGettingStartedScreen}
        />
        <AppStack.Screen name="PaidCallsIntroScreen" component={PaidCallsIntroScreen} />
        <AppStack.Screen name="CallSchedulingReviewScreen" component={CallSchedulingReviewScreen} />
        <AppStack.Screen
          name="CallSchedulingConfirmationScreen"
          component={CallSchedulingConfirmationScreen}
        />
        <AppStack.Screen name="VideoCallCheckout" component={VideoCallCheckoutScreen} />
        <AppStack.Screen name="ReferralVideoCall" component={ReferralVideoCallScreen} />
        <AppStack.Screen
          name="ReferralVideoCallNotLicensed"
          component={ReferralVideoCallNotLicensedScreen}
        />
        <AppStack.Screen name="MemberNoteDetail" component={MemberNoteDetail} />
        <AppStack.Screen name="MemberNotes" component={MemberNotes} />
        <AppStack.Screen name="InsightsSettings" component={InsightsSettings} />
        <AppStack.Screen name="InsightsDetailedPage" component={InsightsDetailsScreen} />
        <AppStack.Screen
          name="MonthlyNutritionCoachingCheckout"
          component={MonthlyNutritionCoachingCheckoutScreen}
        />
        <AppStack.Screen name="AddonCheckout" component={AddonCheckoutScreen} />
        <AppStack.Screen name="SteloAddonOffer" component={SteloAddonOfferScreen} />
        <AppStack.Screen name="DietitianMenuCheckout" component={DietitianMenuCheckoutScreen} />
        <AppStack.Screen
          name="CheckoutWithInsuranceOption"
          component={CheckoutWithInsuranceOptionScreen}
        />
        <AppStack.Screen
          name="DietitianMenuFeedback"
          component={DietitianMenuFeedbackModal}
          options={fadeFromBottomModalOptions}
        />
        <AppStack.Screen name="LabResultsScreen" component={LabResultsScreen} />
        <AppStack.Screen name="BiomarkersScreen" component={BiomarkersScreen} />
        <AppStack.Screen name="BiomarkerForm" component={BiomarkerForm} />
        <AppStack.Screen name="AllVideoCalls" component={AllVideoCalls} />
        <AppStack.Screen name="UpsertRecurrence" component={UpsertRecurrence} />
        <AppStack.Screen name="SetWeightLossGoal" component={SetWeightLossGoalScreen} />
        <AppStack.Screen
          name="ConfirmWeightLossRecurrence"
          component={ConfirmWeightLossRecurrenceScreen}
        />
        <AppStack.Screen name="InsuranceStatus" component={InsuranceStatusScreen} />
        <AppStack.Screen name="ConfirmSensorSelection" component={ConfirmSensorSelectionScreen} />
        <AppStack.Screen name="HealthPermission" component={HealthPermissionScreen} />
        <AppStack.Screen name="SubmitInsuranceCardScreen" component={SubmitInsuranceCardScreen} />
        <AppStack.Screen name="InsurancePolicyForm" component={InsurancePolicyForm} />
        <AppStack.Screen
          name="WeightLossExperienceDesiredWeightCompleted"
          component={WeightLossExperienceDesiredWeightCompletedScreen}
        />
        <AppStack.Screen
          name="WeightLossExperiencePersonalPlan"
          component={WeightLossExperiencePersonalPlanScreen}
        />
        <AppStack.Screen name="SteloCheckout" component={SteloCheckoutScreen} />
        <AppStack.Screen name="SteloSelectSubscription" component={SteloSelectSubscriptionScreen} />
        <AppStack.Screen name="ProgramError" component={ProgramErrorScreen} />
        <AppStack.Screen name="VideoCallsSteloUpsell" component={SteloUpsellScreen} />
        <AppStack.Screen name="SendMessageToCoach" component={SendMessageToCoach} />
        <AppStack.Screen name="TrustpilotReview" component={TrustpilotReview} />
        <AppStack.Screen name="CompletedScreen" component={CompletedScreen} />
        <AppStack.Screen name="LabPanelsScreen" component={LabPanelsScreen} />
        <AppStack.Screen name="LabPanelsOrderConfirmed" component={LabPanelsOrderConfirmed} />
        <AppStack.Screen name="MicrobiomeTest" component={MicrobiomeTest} />
        <AppStack.Screen name="LabPanelRecurrence" component={LabPanelRecurrence} />
        <AppStack.Screen
          name="UnifiedOnboardingSelectNutritionist"
          component={UnifiedOnboardingSelectNutritionist}
        />
      </AppStack.Group>
    </>
  )
}
